import axios from "axios";
import { API_ENDPOINT } from "../constants/constants";
import { getCookie, writeHeartbeat, readHeartbeat, readBreakState, writeBreakState } from "./cookies";

const isValid = (v) => {
  if (v === null || v === undefined) return null;
  const t = String(v).trim();
  if (!t || t === "null" || t === "undefined") return null;
  return t;
};

const EXEMPT_VERIFIER_IDS = [
  "5d6df589c3d92110fccfde7c",
  "688d9d4acebbc60f077de5e2",
  "688d9c64cebbc60f077de5de",
];

let _instance = null;

class IdleTimer {
  constructor() {
    if (_instance) return _instance;
    _instance = this;
    this.verifierId = isValid(getCookie("verifierid"));
    this.verifierType = getCookie("verifiertype");
    this.loginedDateTime = isValid(getCookie("loginedDateTime"));
    const cookieLoginTime = getCookie("loggedInTime");
    this.loginTimeMs = cookieLoginTime ? new Date(cookieLoginTime).getTime() : Date.now();

    if (
      !this.verifierId ||
      !this.loginedDateTime ||
      this.verifierType === "7" ||
      EXEMPT_VERIFIER_IDS.includes(this.verifierId)
    ) {
      this.disabled = true;
      return;
    }

    this.idleThreshold = 3 * 60 * 1000;
    this.breakThreshold = 60 * 1000;

    this.events = ["mousemove", "mousedown", "keypress", "scroll", "touchstart", "click"];
    this.eventHandler = this.onActivity.bind(this);
    this.visibilityHandler = this.handleVisibility.bind(this);

    const shared = readBreakState();
    this.breakTime = Math.max(this.getStoredBreakTime(), shared.breakTime);
    this.isOnBreak = shared.isOnBreak;
    this.breakStartTime = shared.breakStartTime;
    this.lastActivityTime = Date.now();
    this.activityPercentage = 100;

    this.remainingTime = this.idleThreshold;
    this.countdownInterval = null;
    this.breakCheckInterval = null;
    this.activityInterval = null;
    this.hiddenLogoutTimer = null;
    this.stopped = false;

    this.start();
  }

  start() {
    if (this.disabled) return;
    this.events.forEach((e) => window.addEventListener(e, this.eventHandler));
    document.addEventListener("visibilitychange", this.visibilityHandler);

    this._beaconHandler = () => this.fireBeacon();
    window.addEventListener("pagehide", this._beaconHandler);
    window.addEventListener("beforeunload", this._beaconHandler);

    this.startCountdown();
    this.activityInterval = setInterval(() => this.updateActivityLevel(), 60000);
  }

  onActivity() {
    if (this.stopped) return;
    this.remainingTime = this.idleThreshold;
    writeHeartbeat();
    const now = Date.now();

    if (this.isOnBreak) this.endBreak();

    if (!this.lastActivityTime || now - this.lastActivityTime > 500) {
      const previous = this.lastActivityTime;
      this.lastActivityTime = now;

      const totalMin = (now - this.loginTimeMs) / 60000;
      const safeBreak = Math.min(
        Math.max(0, Number(this.breakTime) || 0),
        Math.max(0, totalMin)
      );
      this.breakTime = safeBreak;
      const activeMin = totalMin - safeBreak;

      let pct = totalMin > 0 ? (activeMin / totalMin) * 100 : 100;
      const current = parseInt(localStorage.getItem("report_activityPercentage"), 10) || 0;
      pct = Math.max(pct, current - 5);
      if (previous && now - previous < 30000) pct += 1;
      pct = Math.max(0, Math.min(100, Math.round(pct)));

      this.activityPercentage = pct;
      localStorage.setItem("report_activityPercentage", pct.toString());
    }
  }

  startCountdown() {
    this.clearTimers();

    this.countdownInterval = setInterval(() => {
      this.remainingTime -= 1000;
      if (this.remainingTime <= 0) this.stopTracking();
    }, 1000);

    this.breakCheckInterval = setInterval(() => {
      const lastShared = Math.max(this.lastActivityTime, readHeartbeat());
      const since = Date.now() - lastShared;
      const shared = readBreakState();

      // Mirror cookie → local in BOTH directions. Without the false-side
      // mirror, when another tab ends a break we'd still call endBreak()
      // ourselves and re-add the full duration, double-counting it.
      if (shared.isOnBreak && !this.isOnBreak) {
        this.isOnBreak = true;
        this.breakStartTime = shared.breakStartTime;
      } else if (!shared.isOnBreak && this.isOnBreak) {
        this.isOnBreak = false;
        this.breakStartTime = null;
        this.breakTime = Math.max(this.breakTime || 0, shared.breakTime || 0);
        return;
      }

      if (since >= this.breakThreshold && !this.isOnBreak) {
        this.startBreak();
      } else if (since < this.breakThreshold && this.isOnBreak) {
        this.endBreak();
      }
    }, 1000);
  }

  clearTimers() {
    if (this.countdownInterval) clearInterval(this.countdownInterval);
    if (this.breakCheckInterval) clearInterval(this.breakCheckInterval);
    if (this.hiddenLogoutTimer) clearTimeout(this.hiddenLogoutTimer);
    this.hiddenLogoutTimer = null;
  }

  handleVisibility() {
    if (this.stopped) return;
    if (document.hidden) {
      if (!this.hiddenLogoutTimer) {
        this.hiddenLogoutTimer = setTimeout(() => {
          this.hiddenLogoutTimer = null;
          this.stopTracking();
        }, this.idleThreshold);
      }
    } else {
      if (this.hiddenLogoutTimer) {
        clearTimeout(this.hiddenLogoutTimer);
        this.hiddenLogoutTimer = null;
      }
      this.onActivity();
    }
  }

  stopTracking() {
    if (this.stopped) return;
    this.stopped = true;
    this.clearTimers();
    if (this.activityInterval) clearInterval(this.activityInterval);
    this.events.forEach((e) => window.removeEventListener(e, this.eventHandler));
    document.removeEventListener("visibilitychange", this.visibilityHandler);
    if (this._beaconHandler) {
      window.removeEventListener("pagehide", this._beaconHandler);
      window.removeEventListener("beforeunload", this._beaconHandler);
    }
    this.fireBeacon();
  }

  updateActivityLevel() {
    if (this.stopped) return;
    const now = Date.now();
    const totalMin = (now - this.loginTimeMs) / 60000;
    // Pull the latest shared break total so this tab's POST reflects
    // any break time another tab has accumulated since we last synced.
    const shared = readBreakState();
    this.breakTime = Math.max(this.breakTime || 0, shared.breakTime || 0);
    const safeBreak = Math.min(
      Math.max(0, Number(this.breakTime) || 0),
      Math.max(0, totalMin)
    );
    this.breakTime = safeBreak;
    const activeMin = totalMin - safeBreak;

    let pct = totalMin > 0 ? (activeMin / totalMin) * 100 : 100;
    const since = now - this.lastActivityTime;
    if (since <= 30000) pct += 2;
    else if (since > this.breakThreshold) pct -= 5;
    pct = Math.max(0, Math.min(100, Math.round(Number.isFinite(pct) ? pct : 0)));

    this.activityPercentage = pct;
    localStorage.setItem("report_activityPercentage", pct.toString());

    axios
      .post(`${API_ENDPOINT}/users/UpdateVerifierActivity`, {
        Verifier_id: this.verifierId,
        LoginedTime: this.loginedDateTime,
        ActivityLevel: pct,
        BreaksConsumed: Math.round(safeBreak),
      })
      .catch(() => {});
  }

  startBreak() {
    if (this.isOnBreak) return;
    // Re-check via cookie — another tab may have just started one. If so,
    // adopt its breakStartTime rather than creating a second start.
    const shared = readBreakState();
    if (shared.isOnBreak && shared.breakStartTime) {
      this.isOnBreak = true;
      this.breakStartTime = shared.breakStartTime;
      return;
    }
    this.isOnBreak = true;
    this.breakStartTime = Date.now();
    writeBreakState({ isOnBreak: true, breakStartTime: this.breakStartTime });
    localStorage.setItem("report_breakStartTime", this.breakStartTime.toString());
    localStorage.setItem("report_isOnBreak", "true");
  }

  endBreak() {
    if (!this.isOnBreak || !this.breakStartTime) return;
    const shared = readBreakState();
    // Idempotency guard: if the cookie already shows the break ended,
    // another tab beat us to it and already added the duration. Just sync
    // the merged total without re-adding our local duration.
    if (!shared.isOnBreak) {
      this.isOnBreak = false;
      this.breakStartTime = null;
      this.breakTime = Math.max(this.breakTime || 0, shared.breakTime || 0);
      localStorage.setItem("report_breakTime", this.breakTime.toString());
      localStorage.removeItem("report_breakStartTime");
      localStorage.setItem("report_isOnBreak", "false");
      return;
    }
    const duration = (Date.now() - this.breakStartTime) / 60000;
    const base = Math.max(parseFloat(this.breakTime) || 0, shared.breakTime || 0);
    this.breakTime = Math.round((base + duration) * 100) / 100;
    writeBreakState({
      breakTime: this.breakTime,
      isOnBreak: false,
      breakStartTime: null,
    });
    localStorage.setItem("report_breakTime", this.breakTime.toString());

    axios
      .post(`${API_ENDPOINT}/users/UpdateVerifierActivity`, {
        Verifier_id: this.verifierId,
        LoginedTime: this.loginedDateTime,
        ActivityLevel: Math.round(this.activityPercentage || 0),
        BreaksConsumed: Math.round(this.breakTime),
      })
      .catch(() => {});

    this.isOnBreak = false;
    this.breakStartTime = null;
    localStorage.removeItem("report_breakStartTime");
    localStorage.setItem("report_isOnBreak", "false");
  }

  getStoredBreakTime() {
    const stored = parseFloat(localStorage.getItem("report_breakTime"));
    return Number.isFinite(stored) ? Math.round(stored * 100) / 100 : 0;
  }

  fireBeacon() {
    try {
      if (!this.verifierId || !this.loginedDateTime) return;
      const payload = (obj) =>
        new Blob([JSON.stringify(obj)], { type: "application/json" });
      navigator.sendBeacon(
        `${API_ENDPOINT}/users/UpdateVerifierActivity`,
        payload({
          Verifier_id: this.verifierId,
          LoginedTime: this.loginedDateTime,
          ActivityLevel: Math.round(this.activityPercentage || 0),
          BreaksConsumed: Math.round(this.breakTime || 0),
        })
      );
    } catch (_) {}
  }
}

export default IdleTimer;
